import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    ButtonBase,
    CircularProgress,
    Collapse,
    Dialog,
    DialogContent,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    AccessTime,
    Add,
    ErrorOutline,
    Inventory2Outlined,
    LocalOfferOutlined,
    PersonOutline,
    ReceiptLongOutlined,
    Repeat,
} from "@mui/icons-material";
import { orderService } from "../../../services/orderService";
import { customerService } from "../../../services/customerService";
import { TaskDetail, TaskInput, TaskPriority, TaskRepeat, TaskStatus, taskService } from "../../../services/taskService";
import { isSubmitShortcut, SUBMIT_SHORTCUT_LABEL } from "../../../utils/keyboard";
import { KeyHint } from "../../../components/common/KeyHint";
import { useTaskStore } from "../TaskContext";
import { parseQuickAdd, QuickAddPerson } from "../quickAddParser";
import { apiErrorMessage, formatDue, personName, PRIORITY_META, repeatLabel, STATUS_META } from "../taskUtils";
import { DueMenu, LabelsMenu, PeopleMenu, PriorityMenu, RepeatMenu } from "./Pickers";
import { PriorityFlag, StaffAvatar } from "./TaskBits";

/** Starting values — e.g. from a column's "+" or an order's "Add task". */
export interface QuickAddDefaults {
    status?: TaskStatus;
    assigneeId?: string | null;
    orderId?: string;
    orderNumber?: string;
    customerId?: string;
    customerName?: string;
    productId?: string;
    productName?: string;
    /** Typed into the box to start with, e.g. "ORD202610260013: ". */
    text?: string;
}

type Manual = Partial<{
    assigneeId: string | null;
    dueDate: string | null;
    dueTime: string | null;
    priority: TaskPriority;
    repeat: { rule: TaskRepeat | null; interval: number };
    labels: string[];
}>;

type Found = { status: "idle" | "looking" | "found" | "missing"; id?: string; label?: string };

interface QuickAddProps {
    variant: "bar" | "dialog";
    defaults?: QuickAddDefaults;
    autoFocus?: boolean;
    onCreated: (task: TaskDetail) => void;
    onCancel?: () => void;
    /** The bar collapses its extras until focused. */
    placeholder?: string;
}

export const QuickAdd: React.FC<QuickAddProps> = ({ variant, defaults, autoFocus, onCreated, onCancel, placeholder }) => {
    const { today, assignable, meId, toast } = useTaskStore();
    const [text, setText] = useState(defaults?.text || "");
    const [details, setDetails] = useState("");
    const [showDetails, setShowDetails] = useState(false);
    const [manual, setManual] = useState<Manual>({});
    const [menu, setMenu] = useState<{ key: "assignee" | "due" | "priority" | "repeat" | "labels"; anchor: HTMLElement } | null>(null);
    const [saving, setSaving] = useState(false);
    const [focused, setFocused] = useState(false);
    const [order, setOrder] = useState<Found>({ status: "idle" });
    const [customer, setCustomer] = useState<Found>({ status: "idle" });
    const inputRef = useRef<HTMLInputElement>(null);

    const people: QuickAddPerson[] = useMemo(
        () => assignable.map((p) => ({ user_id: p.user_id, name: personName(p), email: p.email })),
        [assignable],
    );
    const parsed = useMemo(() => parseQuickAdd(text, { today, people, meId }), [text, today, people, meId]);

    // What will be saved: picked values win over typed ones, typed over defaults.
    const final = {
        assigneeId: "assigneeId" in manual ? manual.assigneeId ?? null : parsed.assigneeId ?? defaults?.assigneeId ?? null,
        dueDate: "dueDate" in manual ? manual.dueDate ?? null : parsed.dueDate ?? null,
        dueTime: "dueTime" in manual ? manual.dueTime ?? null : parsed.dueTime ?? null,
        priority: manual.priority ?? parsed.priority ?? "NORMAL",
        repeat: manual.repeat ?? (parsed.repeat ? { rule: parsed.repeat.rule as TaskRepeat | null, interval: parsed.repeat.interval } : { rule: null, interval: 1 }),
        labels: manual.labels ?? parsed.labels,
    };
    // A time with no date means today.
    const dueDate = final.dueDate ?? (final.dueTime ? today : null);

    // Look up a typed order number / phone so the task links to it.
    useEffect(() => {
        const number = parsed.orderNumber;
        if (!number || number === defaults?.orderNumber) {
            setOrder({ status: "idle" });
            return;
        }
        setOrder({ status: "looking" });
        const timer = setTimeout(async () => {
            try {
                const res = await orderService.searchOrders({ search_query: number, limit: 5 });
                const hit = res.items.find((o) => o.order_number.toUpperCase() === number);
                setOrder(hit ? { status: "found", id: hit.order_id, label: hit.order_number } : { status: "missing" });
            } catch {
                setOrder({ status: "missing" });
            }
        }, 350);
        return () => clearTimeout(timer);
    }, [parsed.orderNumber, defaults?.orderNumber]);

    useEffect(() => {
        const phone = parsed.phone;
        if (!phone || defaults?.customerId) {
            setCustomer({ status: "idle" });
            return;
        }
        setCustomer({ status: "looking" });
        const timer = setTimeout(async () => {
            try {
                const res = await customerService.getCustomers({ search: phone, limit: 5 });
                const digits = (s?: string) => (s || "").replace(/\D/g, "").slice(-11);
                const hit = (res.customers || []).find((c) => digits(c.phone) === phone || digits(c.alternative_phone) === phone);
                setCustomer(
                    hit
                        ? { status: "found", id: hit.customer_id, label: [hit.first_name, hit.last_name].filter(Boolean).join(" ") }
                        : { status: "missing" },
                );
            } catch {
                setCustomer({ status: "missing" });
            }
        }, 350);
        return () => clearTimeout(timer);
    }, [parsed.phone, defaults?.customerId]);

    const reset = () => {
        setText(defaults?.text || "");
        setDetails("");
        setShowDetails(false);
        setManual({});
    };

    const submit = async () => {
        const title = parsed.title.trim();
        if (!title || saving) {
            if (!title) toast({ message: "Give the task a title.", severity: "warning" });
            return;
        }
        const input: TaskInput = {
            title: title.slice(0, 200),
            description: details.trim() || null,
            status: defaults?.status ?? "TODO",
            priority: final.priority,
            assignee_user_id: final.assigneeId,
            due_date: dueDate,
            due_time: dueDate ? final.dueTime : null,
            labels: final.labels,
            repeat_rule: final.repeat.rule,
            repeat_interval: final.repeat.interval,
            order_id: defaults?.orderId ?? (order.status === "found" ? order.id : null),
            customer_id: defaults?.customerId ?? (customer.status === "found" ? customer.id : null),
            product_id: defaults?.productId ?? null,
        };
        setSaving(true);
        try {
            const task = await taskService.create(input);
            onCreated(task);
            reset();
            if (variant === "bar") requestAnimationFrame(() => inputRef.current?.focus());
        } catch (err) {
            toast({ message: apiErrorMessage(err, "Couldn't create the task."), severity: "error" });
        } finally {
            setSaving(false);
        }
    };

    const assignee = final.assigneeId ? assignable.find((p) => p.user_id === final.assigneeId) : undefined;
    const expanded = variant === "dialog" || focused || !!text.trim() || !!menu;
    const open = (key: NonNullable<typeof menu>["key"]) => (e: React.MouseEvent<HTMLElement>) => setMenu({ key, anchor: e.currentTarget });

    const chips = (
        <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.75} alignItems="center">
            <PropChip
                active={!!final.assigneeId}
                icon={final.assigneeId ? <StaffAvatar person={assignee} userId={final.assigneeId} size={18} tooltip={false} /> : <PersonOutline sx={{ fontSize: 17 }} />}
                label={assignee ? (assignee.user_id === meId ? `${personName(assignee)} (you)` : personName(assignee)) : "Assignee"}
                onClick={open("assignee")}
                hint="@name"
            />
            <PropChip
                active={!!dueDate}
                icon={<AccessTime sx={{ fontSize: 17 }} />}
                label={dueDate ? formatDue({ due_date: dueDate, due_time: final.dueTime }, today) : "Due"}
                onClick={open("due")}
                hint="tomorrow 4pm"
            />
            <PropChip
                active={final.priority !== "NORMAL"}
                icon={<PriorityFlag priority={final.priority} size={17} />}
                label={final.priority === "NORMAL" ? "Priority" : PRIORITY_META[final.priority].label}
                onClick={open("priority")}
                hint="!high"
            />
            <PropChip
                active={!!final.repeat.rule}
                icon={<Repeat sx={{ fontSize: 17 }} />}
                label={final.repeat.rule ? repeatLabel(final.repeat.rule, final.repeat.interval, dueDate) : "Repeat"}
                onClick={open("repeat")}
                hint="every day"
            />
            <PropChip
                active={final.labels.length > 0}
                icon={<LocalOfferOutlined sx={{ fontSize: 16 }} />}
                label={final.labels.length ? final.labels.map((l) => `#${l}`).join(" ") : "Labels"}
                onClick={open("labels")}
                hint="#label"
            />
            {(defaults?.orderId || order.status !== "idle") && (
                <LinkStatus icon={<ReceiptLongOutlined sx={{ fontSize: 16 }} />} found={defaults?.orderId ? { status: "found", label: defaults.orderNumber } : order} what="order" />
            )}
            {(defaults?.customerId || customer.status !== "idle") && (
                <LinkStatus
                    icon={<PersonOutline sx={{ fontSize: 16 }} />}
                    found={defaults?.customerId ? { status: "found", label: defaults.customerName } : customer}
                    what="customer"
                />
            )}
            {defaults?.productId && <LinkStatus icon={<Inventory2Outlined sx={{ fontSize: 16 }} />} found={{ status: "found", label: defaults.productName }} what="product" />}
            {parsed.unknownPerson && !("assigneeId" in manual) && (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: "error.main" }}>
                    <ErrorOutline sx={{ fontSize: 16 }} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        No one called “{parsed.unknownPerson}”
                    </Typography>
                </Stack>
            )}
            {defaults?.status && defaults.status !== "TODO" && (
                <Typography variant="caption" color="text.secondary">
                    → {STATUS_META[defaults.status].label}
                </Typography>
            )}
        </Stack>
    );

    const input = (
        <TextField
            inputRef={inputRef}
            fullWidth
            autoFocus={autoFocus}
            placeholder={placeholder || "Add a task… e.g. Call customer @Rina tomorrow 4pm !high #delivery"}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={(e) => {
                if ((e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) || isSubmitShortcut(e.nativeEvent)) {
                    e.preventDefault();
                    submit();
                } else if (e.key === "Escape") {
                    if (text && variant === "bar") {
                        e.stopPropagation();
                        reset();
                    } else if (variant === "bar") {
                        (e.target as HTMLElement).blur();
                    }
                }
            }}
            slotProps={{
                input: {
                    startAdornment: <Add sx={{ color: "text.secondary", mr: 1 }} />,
                    endAdornment: saving ? <CircularProgress size={18} /> : null,
                    sx: { borderRadius: 2.5, bgcolor: "background.paper", fontSize: 15 },
                },
                htmlInput: { "aria-label": "New task" },
            }}
        />
    );

    const menus = (
        <>
            <PeopleMenu
                anchorEl={menu?.key === "assignee" ? menu.anchor : null}
                onClose={() => setMenu(null)}
                value={final.assigneeId}
                onSelect={(id) => setManual((m) => ({ ...m, assigneeId: id }))}
            />
            <DueMenu
                anchorEl={menu?.key === "due" ? menu.anchor : null}
                onClose={() => setMenu(null)}
                date={dueDate}
                time={final.dueTime}
                today={today}
                onSelect={(d, t) => setManual((m) => ({ ...m, dueDate: d, dueTime: t }))}
            />
            <PriorityMenu
                anchorEl={menu?.key === "priority" ? menu.anchor : null}
                onClose={() => setMenu(null)}
                value={final.priority}
                onSelect={(p) => setManual((m) => ({ ...m, priority: p }))}
            />
            <RepeatMenu
                anchorEl={menu?.key === "repeat" ? menu.anchor : null}
                onClose={() => setMenu(null)}
                rule={final.repeat.rule}
                interval={final.repeat.interval}
                dueDate={dueDate}
                today={today}
                onSelect={(rule, interval) => setManual((m) => ({ ...m, repeat: { rule, interval } }))}
            />
            <LabelsMenu
                anchorEl={menu?.key === "labels" ? menu.anchor : null}
                onClose={() => setMenu(null)}
                value={final.labels}
                onChange={(labels) => setManual((m) => ({ ...m, labels }))}
            />
        </>
    );

    if (variant === "bar") {
        return (
            <Box>
                {input}
                <Collapse in={expanded}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ pt: 1, px: 0.5 }}>
                        <Box sx={{ flex: 1 }}>{chips}</Box>
                        <Typography variant="caption" color="text.disabled" sx={{ display: { xs: "none", md: "block" }, whiteSpace: "nowrap" }}>
                            Enter to add
                        </Typography>
                    </Stack>
                </Collapse>
                {menus}
            </Box>
        );
    }

    return (
        <Box>
            {input}
            <Box sx={{ mt: 1.25 }}>{chips}</Box>
            <Collapse in={showDetails}>
                <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Details (optional) — Markdown works"
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    onKeyDown={(e) => {
                        if (isSubmitShortcut(e.nativeEvent)) {
                            e.preventDefault();
                            submit();
                        }
                    }}
                    sx={{ mt: 1.5 }}
                    slotProps={{ htmlInput: { maxLength: 10000 } }}
                />
            </Collapse>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2 }}>
                {!showDetails && (
                    <Button size="small" onClick={() => setShowDetails(true)} sx={{ textTransform: "none" }}>
                        Add details
                    </Button>
                )}
                <Box sx={{ flex: 1 }} />
                {onCancel && (
                    <Button color="inherit" onClick={onCancel}>
                        Cancel
                    </Button>
                )}
                <Button variant="contained" onClick={submit} disabled={!parsed.title.trim() || saving}>
                    Create task <KeyHint>{showDetails ? SUBMIT_SHORTCUT_LABEL : "Enter"}</KeyHint>
                </Button>
            </Stack>
            {menus}
        </Box>
    );
};

const PropChip: React.FC<{ active: boolean; icon: React.ReactNode; label: string; hint: string; onClick: (e: React.MouseEvent<HTMLElement>) => void }> = ({
    active,
    icon,
    label,
    hint,
    onClick,
}) => (
    <Tooltip title={active ? "Change" : `Pick, or type ${hint}`}>
        <ButtonBase
            onClick={onClick}
            onMouseDown={(e) => e.preventDefault()}
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.6,
                height: 28,
                px: 1.1,
                borderRadius: 99,
                border: "1px solid",
                borderColor: active ? "#18383E33" : "divider",
                bgcolor: active ? "#e9f1ef" : "transparent",
                color: active ? "#18383E" : "text.secondary",
                fontSize: 13,
                fontWeight: active ? 600 : 500,
                maxWidth: 260,
                "& > span": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
                "&:hover": { borderColor: "#18383E66" },
                "&:focus-visible": { outline: "2px solid #18383E", outlineOffset: 1 },
            }}
        >
            {icon}
            <span>{label}</span>
        </ButtonBase>
    </Tooltip>
);

const LinkStatus: React.FC<{ icon: React.ReactNode; found: Found; what: string }> = ({ icon, found, what }) => (
    <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{
            height: 28,
            px: 1.1,
            borderRadius: 99,
            border: "1px solid",
            borderColor: found.status === "missing" ? "#fecaca" : "#18383E33",
            bgcolor: found.status === "missing" ? "#fef2f2" : "#e9f1ef",
            color: found.status === "missing" ? "error.main" : "#18383E",
            fontSize: 13,
            fontWeight: 600,
        }}
    >
        {icon}
        <span>
            {found.status === "looking" ? `Finding the ${what}…` : found.status === "missing" ? `No ${what} found` : found.label || `Linked ${what}`}
        </span>
    </Stack>
);

// ── Dialog: from the header, the T key, a column's "+", a record ───────────

interface QuickAddDialogProps {
    open: boolean;
    onClose: () => void;
    defaults?: QuickAddDefaults;
    onCreated: (task: TaskDetail) => void;
}

export const QuickAddDialog: React.FC<QuickAddDialogProps> = ({ open, onClose, defaults, onCreated }) => {
    const contentRef = useRef<HTMLDivElement>(null);
    return (
    <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        slotProps={{
            paper: { sx: { borderRadius: 3, alignSelf: "flex-start", mt: { xs: 2, sm: 12 } } },
            // The focus trap wins over autoFocus — focus once it has opened.
            transition: { onEntered: () => contentRef.current?.querySelector<HTMLInputElement>("input")?.focus() },
        }}
    >
        <DialogContent ref={contentRef} sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                New task
            </Typography>
            {open && (
                <QuickAdd
                    variant="dialog"
                    autoFocus
                    defaults={defaults}
                    onCancel={onClose}
                    onCreated={(task) => {
                        onCreated(task);
                        onClose();
                    }}
                />
            )}
        </DialogContent>
    </Dialog>
    );
};

/** Small framed box used where a bar sits on a coloured background. */
export const QuickAddCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3 }}>
        {children}
    </Paper>
);
